import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useCatalogData } from "./CatalogDataContext";
import {
  MIXED_MODALITY_COMPARE_MSG,
  compareIdsAreMixed,
  isMixedModalityCompare,
  wouldMixModality,
} from "../lib/compareModality";

const MAX = 4;

type CompareCtx = {
  selected: string[];
  toggle: (id: string) => void;
  clear: () => void;
  isSelected: (id: string) => boolean;
  canAdd: boolean;
  setSelected: (ids: string[]) => void;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  /** Bumps on every openCompare — remount modal local state (query, tab, notes). */
  remapKey: number;
  openCompare: (ids?: string[]) => void;
  /** Banner/toast when checkbox or open would mix image + video. */
  selectionError: string | null;
  clearSelectionError: () => void;
};

const Ctx = createContext<CompareCtx | null>(null);

function persist(ids: string[]) {
  localStorage.setItem("gm-compare", JSON.stringify(ids));
}

export function CompareProvider({ children }: { children: ReactNode }) {
  const { models } = useCatalogData();
  const [selected, setSelectedState] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem("gm-compare");
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [remapKey, setRemapKey] = useState(0);
  const [selectionError, setSelectionError] = useState<string | null>(null);

  const clearSelectionError = useCallback(() => setSelectionError(null), []);

  // Drop persisted mixed image+video sets (pre-rule or stale).
  useEffect(() => {
    if (models.length === 0) return;
    try {
      const raw = localStorage.getItem("gm-compare");
      const ids: string[] = raw ? (JSON.parse(raw) as string[]) : [];
      if (ids.length >= 2 && compareIdsAreMixed(ids, models)) {
        setSelectedState([]);
        persist([]);
        setSelectionError(MIXED_MODALITY_COMPARE_MSG);
      }
    } catch {
      /* ignore */
    }
  }, [models]);

  const setSelected = useCallback(
    (ids: string[]) => {
      const next = [...new Set(ids)].slice(0, MAX);
      if (compareIdsAreMixed(next, models)) {
        setSelectionError(MIXED_MODALITY_COMPARE_MSG);
        return;
      }
      setSelectionError(null);
      setSelectedState(next);
      persist(next);
    },
    [models],
  );

  const toggle = useCallback(
    (id: string) => {
      setSelectedState((prev) => {
        if (prev.includes(id)) {
          setSelectionError(null);
          const next = prev.filter((x) => x !== id);
          persist(next);
          return next;
        }
        if (prev.length >= MAX) return prev;

        const candidate = models.find((m) => m.id === id);
        if (candidate) {
          const existing = prev
            .map((pid) => models.find((m) => m.id === pid))
            .filter((m): m is NonNullable<typeof m> => Boolean(m));
          if (wouldMixModality(existing, candidate)) {
            setSelectionError(MIXED_MODALITY_COMPARE_MSG);
            return prev;
          }
        }

        setSelectionError(null);
        const next = [...prev, id];
        persist(next);
        return next;
      });
    },
    [models],
  );

  const clear = useCallback(() => {
    setSelectedState([]);
    persist([]);
    setSelectionError(null);
  }, []);

  const isSelected = useCallback(
    (id: string) => selected.includes(id),
    [selected],
  );

  const openCompare = useCallback(
    (ids?: string[]) => {
      // Always remount modal chrome so intent remap never leaves stale
      // query text, tab, overlay highlight, or notes.
      setRemapKey((k) => k + 1);
      if (ids && ids.length > 0) {
        const next = [...new Set(ids)].slice(0, MAX);
        if (compareIdsAreMixed(next, models)) {
          setSelectionError(MIXED_MODALITY_COMPARE_MSG);
          // Do not open modal / do not replace selection with a mixed set.
          return;
        }
        setSelectionError(null);
        setSelectedState(next);
        persist(next);
        if (next.length >= 2) setModalOpen(true);
        return;
      }
      if (compareIdsAreMixed(selected, models)) {
        setSelectionError(MIXED_MODALITY_COMPARE_MSG);
        return;
      }
      setModalOpen(true);
    },
    [models, selected],
  );

  const value = useMemo(
    () => ({
      selected,
      toggle,
      clear,
      isSelected,
      canAdd: selected.length < MAX,
      setSelected,
      modalOpen,
      setModalOpen,
      remapKey,
      openCompare,
      selectionError,
      clearSelectionError,
    }),
    [
      selected,
      toggle,
      clear,
      isSelected,
      setSelected,
      modalOpen,
      remapKey,
      openCompare,
      selectionError,
      clearSelectionError,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCompare() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCompare outside provider");
  return ctx;
}

/** Re-export for callers that need the message without importing the lib. */
export { MIXED_MODALITY_COMPARE_MSG, isMixedModalityCompare };
